import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ArrowLeft, ArrowRight } from "lucide-react";

function buildPageItems(currentPage: number, pageCount: number): Array<number | "ellipsis"> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index);
  }

  const pages = new Set<number>([0, 1, pageCount - 2, pageCount - 1, currentPage]);
  if (currentPage - 1 > 0) {
    pages.add(currentPage - 1);
  }
  if (currentPage + 1 < pageCount - 1) {
    pages.add(currentPage + 1);
  }

  const sortedPages = Array.from(pages).sort((left, right) => left - right);
  const items: Array<number | "ellipsis"> = [];

  sortedPages.forEach((page, index) => {
    if (index > 0 && page - sortedPages[index - 1] > 1) {
      items.push("ellipsis");
    }
    items.push(page);
  });

  return items;
}

export function TablePagination({
  currentPage,
  pageCount,
  canPreviousPage,
  canNextPage,
  onPreviousPage,
  onNextPage,
  onPageChange,
}: {
  currentPage: number;
  pageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onPageChange: (pageIndex: number) => void;
}) {
  const pageItems = buildPageItems(currentPage, pageCount);

  return (
    <div className="flex flex-col gap-4 border-t px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
      <Button
        variant="outline"
        size="sm"
        className="h-10 rounded-xl px-4"
        onClick={onPreviousPage}
        disabled={!canPreviousPage}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Previous
      </Button>

      <div className="flex items-center justify-center gap-2">
        {pageItems.map((item, index) =>
          item === "ellipsis" ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-muted-foreground text-sm"
            >
              ...
            </span>
          ) : (
            <button
              key={item}
              type="button"
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-xl text-sm transition-colors",
                item === currentPage
                  ? "bg-brand-yellow font-medium text-brand-darkGreen"
                  : "text-muted-foreground hover:bg-muted",
              )}
              onClick={() => onPageChange(item)}
            >
              {item + 1}
            </button>
          ),
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="h-10 rounded-xl px-4"
        onClick={onNextPage}
        disabled={!canNextPage}
      >
        Next
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
